/**
 * Ordering Element Editor
 * Question input + vertical list of items with up/down/remove controls.
 * The order in the editor IS the correct answer.
 */

import * as React from 'react';
import {
    Button,
    Badge,
    Input,
    Label,
    Textarea,
    cn,
} from '@ai-content-hub/content-kit';
import type { ElementEditorProps } from '@ai-content-hub/content-kit';
import { Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import {
    type OrderingElementData,
    normalizeOrderingData,
    createDefaultOrderingItem,
    MIN_ITEMS,
    MAX_ITEMS,
    orderingElementSchema,
} from './schema';
import { useElementSchemaLocale } from '../i18n/useElementSchemaLocale';

export type OrderingEditProps = ElementEditorProps<OrderingElementData>;

export const OrderingEdit: React.FC<OrderingEditProps> = ({
    value,
    onChange,
    disabled = false,
    className,
    errors = {},
    config,
    schema: schemaProp,
    language,
}) => {
    const schema = schemaProp ?? orderingElementSchema;
    const { t } = useElementSchemaLocale(schema.i18n, { config, language });
    const data = React.useMemo(() => normalizeOrderingData(value || {}), [value]);

    const updateData = (updates: Partial<OrderingElementData>) => {
        onChange({ ...data, ...updates });
    };

    const addItem = () => {
        if (disabled || data.items.length >= MAX_ITEMS) return;
        const newItem = createDefaultOrderingItem(data.items.length);
        updateData({ items: [...data.items, newItem] });
    };

    const removeItem = (itemId: string) => {
        if (disabled || data.items.length <= MIN_ITEMS) return;
        updateData({ items: data.items.filter((i) => i.id !== itemId) });
    };

    const updateItemText = (itemId: string, text: string) => {
        if (disabled) return;
        updateData({
            items: data.items.map((i) => (i.id === itemId ? { ...i, text } : i)),
        });
    };

    const moveUp = (index: number) => {
        if (disabled || index === 0) return;
        const next = [...data.items];
        [next[index - 1], next[index]] = [next[index], next[index - 1]];
        updateData({ items: next });
    };

    const moveDown = (index: number) => {
        if (disabled || index === data.items.length - 1) return;
        const next = [...data.items];
        [next[index], next[index + 1]] = [next[index + 1], next[index]];
        updateData({ items: next });
    };

    return (
        <div className={cn('ordering-edit', 'flex flex-col gap-6', className)}>
            {/* Question */}
            <div className={cn('ordering-edit-question', 'space-y-2')}>
                <Label htmlFor="ordering-question" className="text-lg font-semibold">
                    {t('question')}
                </Label>
                <div className="flex gap-2">
                    <Input
                        id="ordering-question"
                        value={data.question}
                        onChange={(e) => updateData({ question: e.target.value })}
                        placeholder={t('enterQuestionInstruction')}
                        disabled={disabled}
                        aria-invalid={Boolean(errors.question)}
                        className={cn(
                            'ordering-edit-question-input flex-1',
                            errors.question && 'border-destructive',
                        )}
                    />
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-10 w-10 text-primary shrink-0"
                        disabled
                        aria-hidden
                    >
                        <Plus className="h-4 w-4" />
                    </Button>
                </div>
                {errors.question ? (
                    <p
                        className="ordering-edit-question-error text-sm text-destructive"
                        role="alert"
                    >
                        {errors.question}
                    </p>
                ) : null}
            </div>

            {/* Items list */}
            <div className={cn('ordering-edit-items-section', 'flex flex-col gap-4')}>
                {errors.items ? (
                    <p
                        className="ordering-edit-items-error text-sm text-destructive"
                        role="alert"
                    >
                        {errors.items}
                    </p>
                ) : null}
                <div className={cn('ordering-edit-items-list', 'space-y-3')}>
                    {data.items.map((item, index) => {
                        const itemTextError = errors[`items.${index}.text`];
                        return (
                            <div
                                key={item.id}
                                className={cn('ordering-edit-item-row', 'space-y-1')}
                            >
                                <div className="ordering-edit-item flex items-center gap-3">
                                    <Badge
                                        variant="secondary"
                                        className={cn(
                                            'ordering-edit-item-badge',
                                            'bg-info/10 text-info shrink-0 w-7 justify-center font-semibold',
                                        )}
                                    >
                                        {index + 1}.
                                    </Badge>
                                    <Input
                                        value={item.text}
                                        onChange={(e) => updateItemText(item.id, e.target.value)}
                                        placeholder={`Item ${index + 1}`}
                                        disabled={disabled}
                                        aria-invalid={Boolean(itemTextError)}
                                        className={cn(
                                            'ordering-edit-item-input flex-1',
                                            itemTextError && 'border-destructive',
                                        )}
                                    />
                                    <div className="flex items-center gap-0.5 shrink-0">
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7 text-muted-foreground hover:text-foreground"
                                            onClick={() => moveUp(index)}
                                            disabled={disabled || index === 0}
                                            aria-label={t('moveUp')}
                                        >
                                            <ChevronUp className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7 text-muted-foreground hover:text-foreground"
                                            onClick={() => moveDown(index)}
                                            disabled={disabled || index === data.items.length - 1}
                                            aria-label={t('moveDown')}
                                        >
                                            <ChevronDown className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            className={cn(
                                                'ordering-edit-item-remove-button',
                                                'h-7 w-7 text-destructive hover:text-destructive hover:bg-destructive/5 shrink-0',
                                            )}
                                            onClick={() => removeItem(item.id)}
                                            disabled={disabled || data.items.length <= MIN_ITEMS}
                                            aria-label={t('removeItem')}
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                                {itemTextError ? (
                                    <p
                                        className="ordering-edit-item-error text-sm text-destructive pl-10"
                                        role="alert"
                                    >
                                        {itemTextError}
                                    </p>
                                ) : null}
                            </div>
                        );
                    })}
                </div>
                <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className={cn(
                        'ordering-edit-add-item-button',
                        'w-auto self-start border-dashed border-border text-primary'
                    )}
                    onClick={addItem}
                    disabled={disabled || data.items.length >= MAX_ITEMS}
                >
                    <Plus className="h-4 w-4 mr-2" />
                    {t('addItem')}
                </Button>
            </div>

            {/* Explanation */}
            <div className={cn('ordering-edit-explanation', 'space-y-2')}>
                <Label htmlFor="ordering-explanation">{t('explanationAfterSubmit')}</Label>
                <Textarea
                    id="ordering-explanation"
                    value={data.explanation ?? ''}
                    onChange={(e) => updateData({ explanation: e.target.value })}
                    placeholder={t('explainCorrectAnswer')}
                    disabled={disabled}
                    rows={3}
                    className="ordering-edit-explanation-input"
                />
            </div>
        </div>
    );
};

export default OrderingEdit;
